import { moviePlannedToWatchQuery } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { createResourceQuery } from '@funtech-int/rival';
import { useResources } from '@funtech-int/rival-react';
import { useAuthManager } from '@funtech-int/yango-shared';

import { GraphqlClient } from '@/shared/lib/graphqlClient';
import { InMemoryCache } from '@/shared/lib/inMemoryCache';
import { getMovieUGCKey } from '@/shared/lib/movieUGC';
import { presentTitle } from '@/shared/lib/movie';

import { useGraphqlClient } from './useGraphqlClient';
import { useInMemoryCache } from './useInMemoryCache';

interface MoviePlannedToWatchResult {
    title: string | undefined;
    contentId: string | undefined;
}

interface MoviePlannedToWatchQueryContext {
    movieId: number;
    isAuthorized: boolean;
    client: GraphqlClient;
    inMemoryCache: InMemoryCache;
}

const moviePlannedToWatchResourceQuery = createResourceQuery<
    MoviePlannedToWatchQueryContext,
    MoviePlannedToWatchResult
>({
    key(ctx) {
        return [moviePlannedToWatchQuery.name, ctx.movieId, ctx.isAuthorized].join(':');
    },

    fetch(ctx) {
        return ctx.client
            .fetch({
                operation: moviePlannedToWatchQuery,
                variables: { movieId: ctx.movieId, isAuthorized: ctx.isAuthorized },
            })
            .then((response) => {
                const isPlannedToWatch = Boolean(response.data?.isPlannedToWatch);

                ctx.inMemoryCache.set(
                    getMovieUGCKey(ctx.movieId, 'plannedToWatch'),
                    isPlannedToWatch
                );

                return {
                    isPlannedToWatch,
                    title: presentTitle(response.data?.title),
                    contentId: response.data?.contentId ?? undefined,
                };
            });
    },

    toJSON(data) {
        return data as any;
    },

    hydrate(data: any) {
        return data;
    },
});

interface UseMoviePlannedToWatchParams {
    movieId: number;
}

export function useMoviePlannedToWatch({ movieId }: UseMoviePlannedToWatchParams) {
    const client = useGraphqlClient();
    const inMemoryCache = useInMemoryCache();
    const { isAuthorized } = useAuthManager();

    const [result] = useResources(
        moviePlannedToWatchResourceQuery({
            movieId,
            isAuthorized,
            client,
            inMemoryCache,
        })
    );

    return {
        title: result.title,
        contentId: result.contentId,
    };
}
